/**
 * Loads the four Quill style catalogues plus the "Most Popular" badge setting.
 *
 * Unlike the LifeScribe original's data hook this does NOT map rows into a
 * parallel camelCase shape — the page renders the real columns. The original's
 * mapping layer is where the illustration prompt fields went missing on the way
 * back to the server, and where `usageCount: style.usage_count || 0` turned an
 * absent column into a confident zero.
 *
 * Every catalogue loads independently: one failing request leaves the other
 * three usable and records its own error rather than blanking the page.
 */

import { useCallback, useEffect, useState } from 'react';
import { aiStylesApi } from './api';
import { TAB_CONFIGS } from './constants';
import { CatalogueKey, CatalogueResult, PopularCategory } from './types';

export type CatalogueMap = Record<CatalogueKey, CatalogueResult>;

const EMPTY: CatalogueResult = { rows: [], active_count: 0 };

function emptyMap(): CatalogueMap {
  return TAB_CONFIGS.reduce((acc, tab) => {
    acc[tab.catalogue] = EMPTY;
    return acc;
  }, {} as CatalogueMap);
}

export interface UseStylesDataResult {
  catalogues: CatalogueMap;
  popular: PopularCategory;
  setPopularLocal: (next: PopularCategory) => void;
  loading: boolean;
  refreshing: boolean;
  /** Per-catalogue load error, keyed by catalogue. */
  errors: Partial<Record<CatalogueKey, string>>;
  reload: (opts?: { silent?: boolean }) => Promise<void>;
  reloadCatalogue: (catalogue: CatalogueKey) => Promise<void>;
}

export function useStylesData(): UseStylesDataResult {
  const [catalogues, setCatalogues] = useState<CatalogueMap>(emptyMap);
  const [popular, setPopular] = useState<PopularCategory>({ writing: 'genre', illustration: 'style' });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<CatalogueKey, string>>>({});

  const reloadCatalogue = useCallback(async (catalogue: CatalogueKey) => {
    try {
      const result = await aiStylesApi.list(catalogue);
      setCatalogues((prev) => ({ ...prev, [catalogue]: result }));
      setErrors((prev) => {
        if (!prev[catalogue]) return prev;
        const next = { ...prev };
        delete next[catalogue];
        return next;
      });
    } catch (e) {
      setErrors((prev) => ({
        ...prev,
        [catalogue]: e instanceof Error ? e.message : 'Failed to load catalogue.',
      }));
    }
  }, []);

  const reload = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (opts?.silent) setRefreshing(true);
      await Promise.all([
        ...TAB_CONFIGS.map((tab) => reloadCatalogue(tab.catalogue)),
        aiStylesApi
          .getPopularCategory()
          .then((value) => setPopular(value))
          // The badge setting is a nicety — a failure here must not blank the page.
          .catch(() => undefined),
      ]);
      setLoading(false);
      setRefreshing(false);
    },
    [reloadCatalogue]
  );

  useEffect(() => {
    reload();
  }, [reload]);

  return {
    catalogues,
    popular,
    setPopularLocal: setPopular,
    loading,
    refreshing,
    errors,
    reload,
    reloadCatalogue,
  };
}

export default useStylesData;
